import { useState, useEffect } from "react";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, Lock, CalendarClock } from "lucide-react";
import { API } from "../lib/store";

const TOKEN_KEY = "aa_admin_token";

export default function AdminSync() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [status, setStatus] = useState("idle"); // idle | syncing | done | error
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [lastSync, setLastSync] = useState(null);

    useEffect(() => {
        fetch(`${API}/admin/sync-status`)
            .then((r) => r.json())
            .then((d) => setLastSync(d.last_sync))
            .catch(() => {});
    }, [status]);

    const sync = async () => {
        if (!token.trim() || status === "syncing") return;
        localStorage.setItem(TOKEN_KEY, token.trim());
        setStatus("syncing");
        setError("");
        setResult(null);
        try {
            const res = await fetch(`${API}/admin/sync`, {
                method: "POST",
                headers: { "X-Admin-Token": token.trim() },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Sync failed");
            setResult(data);
            setStatus("done");
        } catch (e) {
            setError(String(e.message || e));
            setStatus("error");
        }
    };

    const rows = result?.summary
        ? [
              ["Products in catalog matched", result.summary.matched],
              ["Now in stock", result.summary.total_in_stock],
              ["Stock counts changed", result.summary.stock_changed],
              ["Prices updated", result.summary.price_changed],
              ["Newly back in stock", result.summary.now_in_stock],
              ["Newly sold out", result.summary.now_out_of_stock],
          ]
        : [];

    return (
        <div className="min-h-screen bg-ink text-slate-50 font-sans grain">
            <div className="mx-auto max-w-2xl px-5 py-16 md:py-24">
                <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-gold text-ink font-display font-extrabold">AA</div>
                    <div>
                        <p className="font-display text-xl font-extrabold tracking-tight">Inventory Sync</p>
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Karagna.com · Admin</p>
                    </div>
                </div>

                <div className="mt-10 rounded-2xl border border-white/10 bg-ink2 p-6 md:p-8">
                    <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">
                        Pull live stock &amp; prices <span className="text-gold">from RetailDeck</span>
                    </h1>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">
                        One click refreshes every listing's stock count and price straight from your RetailDeck dealer
                        account. Product photos and titles stay exactly as they are.
                    </p>

                    <div data-testid="nightly-sync-note" className="mt-5 flex items-start gap-3 rounded-xl border border-white/10 bg-ink p-4">
                        <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                        <div className="font-mono text-[11px] leading-relaxed text-slate-400">
                            <span className="text-slate-200">Auto-sync is ON.</span> Stock &amp; prices refresh
                            automatically every night at <span className="text-gold">3:00 AM ET</span>.
                            {lastSync && (
                                <div className="mt-1">
                                    Last sync: {new Date(lastSync.synced_at).toLocaleString()} ·{" "}
                                    {lastSync.ok ? (
                                        <span className="text-save">{lastSync.trigger} · OK</span>
                                    ) : (
                                        <span className="text-hdred">{lastSync.trigger} · failed</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <label className="mt-8 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
                        Admin token
                    </label>
                    <div className="relative mt-2">
                        <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                        <input
                            data-testid="admin-token-input"
                            type="password"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="Enter admin token"
                            className="w-full rounded-lg border border-white/15 bg-ink py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none transition-colors"
                        />
                    </div>

                    <button
                        data-testid="admin-sync-button"
                        onClick={sync}
                        disabled={status === "syncing" || !token.trim()}
                        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-mono text-xs font-bold uppercase tracking-widest text-ink transition-colors hover:bg-white disabled:opacity-50"
                    >
                        {status === "syncing" ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" /> Syncing with RetailDeck…
                            </>
                        ) : (
                            <>
                                <RefreshCw className="h-4 w-4" /> Sync inventory now
                            </>
                        )}
                    </button>
                    {status === "syncing" && (
                        <p className="mt-3 text-center font-mono text-[11px] text-slate-500">
                            Pulling ~11,500 items — this takes up to a minute.
                        </p>
                    )}

                    {status === "error" && (
                        <div
                            data-testid="admin-sync-error"
                            className="mt-6 flex items-start gap-3 rounded-xl border border-hdred/40 bg-hdred/10 p-4 text-sm text-hdred"
                        >
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {status === "done" && result && (
                        <div data-testid="admin-sync-result" className="mt-6">
                            <div className="flex items-center gap-2 text-save">
                                <CheckCircle2 className="h-5 w-5" />
                                <span className="font-display font-bold">Inventory synced</span>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-3">
                                {rows.map(([label, val]) => (
                                    <div key={label} className="rounded-xl border border-white/10 bg-ink p-4">
                                        <p className="font-display text-2xl font-extrabold text-gold">{val}</p>
                                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                                            {label}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600">
                                Synced {new Date(result.synced_at).toLocaleString()}
                            </p>
                        </div>
                    )}
                </div>

                <a href="/" className="mt-8 inline-block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 hover:text-gold transition-colors">
                    ← Back to storefront
                </a>
            </div>
        </div>
    );
}
